import React, { useCallback, useRef, useEffect } from "react";

/**
 * A draggable resize handle for panel resizing.
 * @param {"horizontal"|"vertical"} direction - "horizontal" for left/right panels, "vertical" for timeline
 * @param {function} onResize - Called with delta pixels during drag
 * @param {function} onResizeEnd - Called when drag ends
 * @param {string} className - Additional CSS classes
 */
const ResizeHandle = ({ direction = "horizontal", onResize, onResizeEnd, className = "" }) => {
  const isDragging = useRef(false);
  const lastPos = useRef(0);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    isDragging.current = true;
    lastPos.current = direction === "horizontal" ? e.clientX : e.clientY;
    document.body.style.cursor = direction === "horizontal" ? "col-resize" : "row-resize";
    document.body.style.userSelect = "none";
  }, [direction]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      const currentPos = direction === "horizontal" ? e.clientX : e.clientY;
      const delta = currentPos - lastPos.current;
      lastPos.current = currentPos;
      if (delta !== 0 && onResize) onResize(delta);
    };

    const handleMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      if (onResizeEnd) onResizeEnd();
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [direction, onResize, onResizeEnd]);

  const isHorizontal = direction === "horizontal";

  return (
    <div
      onMouseDown={handleMouseDown}
      className={`resize-handle ${isHorizontal ? "resize-handle-horizontal" : "resize-handle-vertical"} ${className}`}
      style={{
        position: "absolute",
        zIndex: 25,
        ...(isHorizontal
          ? { top: 0, bottom: 0, width: 6, cursor: "col-resize" }
          : { left: 0, right: 0, height: 6, cursor: "row-resize" }),
      }}
    >
      <div
        className="resize-handle-indicator"
        style={{
          position: "absolute",
          borderRadius: 2,
          backgroundColor: "transparent",
          transition: "background-color 0.15s",
          ...(isHorizontal
            ? { top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 2, height: 32 }
            : { left: "50%", top: "50%", transform: "translate(-50%, -50%)", height: 2, width: 32 }),
        }}
      />
    </div>
  );
};

export default ResizeHandle;
